import { useCallback, memo } from 'react';
import { Typography, IconButton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LassieWorkstation } from '@thive/fddm-types';

import { MinusIcon } from '@/assets/icons';
import {
  getMultiSelectDisplayValue,
  MultiSelectInput,
  NumberInput,
} from '@/modules/common/components/inputs';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';

const POSITION_FROM_BOTTOM_MINIMUM_IN_MM = 0;

const workstationTypeOptions: {
  label: string;
  value: LassieWorkstation['type'];
}[] = [
  {
    label: 'SINGLE_RAMP_TYPE_A',
    value: 'SINGLE_RAMP_TYPE_A',
  },
];

type Props = {
  index: number;
  name: string;
  type: LassieWorkstation['type'];
  positionFromBottom: LassieWorkstation['positionFromBottom'];
  disableAllInteraction: boolean;
  updatePosition: (workstationConfigIndex: number, value: number) => void;
  removeWorkstation: (workstationConfigIndex: number) => void;
};

const WorkstationConfigComponent = ({
  index,
  name,
  type,
  positionFromBottom,
  disableAllInteraction,
  updatePosition,
  removeWorkstation,
}: Props) => {
  const { t } = useTranslation();
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars, no-unused-vars
  const onTypeOptionClick = useCallback((value: string) => {
    // temp not hooked up to anything. Waiting for more workstation types to be introduced
  }, []);
  const validateProvidedPosition = useCallback(
    (
      positionFromBottom: number,
    ): { result: 'valid' } | { result: 'invalid'; message: string; alternativeValue: number } => {
      if (positionFromBottom < POSITION_FROM_BOTTOM_MINIMUM_IN_MM) {
        return {
          result: 'invalid',
          message: t('common:minimum_value', {
            minValue: POSITION_FROM_BOTTOM_MINIMUM_IN_MM,
          }),
          alternativeValue: POSITION_FROM_BOTTOM_MINIMUM_IN_MM,
        };
      }

      return {
        result: 'valid',
      };
    },
    [t],
  );

  const onMinusBtnClick = useCallback(() => {
   removeWorkstation(index);
  }, [index, removeWorkstation]);

  const onPositionChange = useCallback(
    (value: number) => {
      const validation = validateProvidedPosition(value);
      if (validation.result === 'invalid') {
        return {
          error: validation.message,
        };
      }

      updatePosition(index, value);
    },
    [index, updatePosition, validateProvidedPosition],
  );

  const workstationTypeDisplayValue = getMultiSelectDisplayValue(
    workstationTypeOptions.map((item) => ({ id: item.value, name: item.label })),
    [type],
  );

  return (
    <Stack gap={1}>
      <Stack direction='row' justifyContent='space-between'>
        <Typography variant='h6'>{name}</Typography>

        <IconButton
          onClick={onMinusBtnClick}
          size='small'
          sx={{ p: 0, height: 'fit-content' }}
          disabled={disableAllInteraction}
        >
          <MinusIcon />
        </IconButton>
      </Stack>

      <PropertiesLabel i18nkey='interface:properties.template.workstation.type'>
        <MultiSelectInput
          selectedOptions={[type]}
          options={workstationTypeOptions}
          onItemClick={onTypeOptionClick}
          displayValue={workstationTypeDisplayValue}
          disabled={disableAllInteraction}
          optionListMinWidth='100px'
        />
      </PropertiesLabel>
      <PropertiesLabel i18nkey='interface:properties.template.workstation.position'>
        <Stack direction='row' gap={1}>
          <NumberInput
            value={positionFromBottom}
            sx={{ width: '100%' }}
            disabled={disableAllInteraction}
            onChange={onPositionChange}
            unit='m'
            useButtons
          />
        </Stack>
      </PropertiesLabel>
    </Stack>
  );
};

export const WorkstationConfig = memo(WorkstationConfigComponent);
